import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Check, X, Users, Trash2, Clock, MapPin } from "lucide-react";

/**
 * Month / week calendar grid for trainer sessions (CRM-style, own implementation).
 *
 * Props:
 *   sessions: [{ id, date 'YYYY-MM-DD', time 'HH:MM', duration, client_name, type, status, location_name? }]
 *   sessionTypeLabels: { type -> label }
 *   view: "month" | "week"
 *   onNewSession?: (dateStr) => void           // click empty day → prefill new session
 *   onEventAction?: (action, session) => void  // 'confirm'|'reject'|'complete'|'cancel'|'reassign'|'delete'
 */

const PL_DAYS_SHORT = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Nd"];
const PL_MONTHS = ["styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec",
  "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień"];

// status → accent colour
const STATUS_COLOR = {
  pending_confirmation: "#f59e0b",
  scheduled: "#6366f1",
  confirmed: "#22c55e",
  completed: "#64748b",
  cancelled: "#ef4444",
  rejected: "#ef4444",
  absent: "#b45309",
};
const STATUS_LABEL = {
  pending_confirmation: "Oczekuje",
  scheduled: "Zaplanowana",
  confirmed: "Potwierdzona",
  completed: "Zakończona",
  cancelled: "Odwołana",
  rejected: "Odrzucona",
  absent: "Nieobecność",
};

const pad = (n) => String(n).padStart(2, "0");
const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (d) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  x.setHours(0, 0, 0, 0);
  return x;
};

export default function SessionCalendar({ sessions = [], sessionTypeLabels = {}, view = "month", onNewSession, onEventAction }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [selected, setSelected] = useState(null);
  const todayKey = toKey(new Date());

  // index sessions by date key
  const byDate = useMemo(() => {
    const m = {};
    for (const s of sessions) {
      if (!s.date) continue;
      (m[s.date] = m[s.date] || []).push(s);
    }
    for (const k in m) m[k].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    return m;
  }, [sessions]);

  const shift = (dir) => {
    const d = new Date(cursor);
    if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCursor(d);
  };
  const goToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setCursor(d); };

  // ---- build cells -----------------------------------------------------------
  let cells = [];
  let periodLabel = "";
  if (view === "week") {
    const start = mondayOf(cursor);
    cells = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
    const end = cells[6];
    periodLabel = start.getMonth() === end.getMonth()
      ? `${start.getDate()}–${end.getDate()} ${PL_MONTHS[start.getMonth()]} ${start.getFullYear()}`
      : `${start.getDate()} ${PL_MONTHS[start.getMonth()]} – ${end.getDate()} ${PL_MONTHS[end.getMonth()]} ${end.getFullYear()}`;
  } else {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = mondayOf(first);
    const weeks = Math.ceil(((((first.getDay() + 6) % 7)) + new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate()) / 7);
    cells = Array.from({ length: weeks * 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
    periodLabel = `${PL_MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  }

  const Chip = ({ s, compact }) => (
    <button type="button" onClick={() => setSelected(s)}
      title={`${s.time} · ${s.client_name}`}
      style={{
        display: "flex", alignItems: "center", gap: 5, width: "100%", textAlign: "left",
        border: "none", cursor: "pointer", borderRadius: 6, padding: compact ? "2px 6px" : "5px 8px",
        marginBottom: 3, fontSize: compact ? 11 : 12, lineHeight: 1.3, color: "#fff",
        background: STATUS_COLOR[s.status] || "#6366f1",
        opacity: s.status === "cancelled" || s.status === "rejected" ? 0.55 : 1,
      }}>
      <span style={{ fontWeight: 700, flexShrink: 0 }}>{s.time}</span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {s.trainer_name ? `${s.trainer_name} · ${s.client_name}` : s.client_name}
      </span>
    </button>
  );

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 4 }}>
          <button type="button" onClick={() => shift(-1)} aria-label="Poprzedni" style={navBtn}><ChevronLeft size={18} /></button>
          <button type="button" onClick={goToday} style={{ ...navBtn, width: "auto", padding: "0 14px", fontSize: 13, fontWeight: 600 }}>Dziś</button>
          <button type="button" onClick={() => shift(1)} aria-label="Następny" style={navBtn}><ChevronRight size={18} /></button>
        </div>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, textTransform: "capitalize" }}>{periodLabel}</h2>
        <div style={{ marginLeft: "auto", display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "var(--text-secondary)" }}>
          {["pending_confirmation", "confirmed", "scheduled", "completed"].map((st) => (
            <span key={st} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: STATUS_COLOR[st] }} />
              {STATUS_LABEL[st]}
            </span>
          ))}
        </div>
      </div>

      {/* Weekday header */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6, marginBottom: 6 }}>
        {PL_DAYS_SHORT.map((d) => (
          <div key={d} style={{ textAlign: "center", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.4 }}>{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6 }}>
        {cells.map((d) => {
          const key = toKey(d);
          const events = byDate[key] || [];
          const inMonth = view === "week" || d.getMonth() === cursor.getMonth();
          const isToday = key === todayKey;
          const isWeekend = (d.getDay() === 0 || d.getDay() === 6);
          const max = view === "week" ? 12 : 3;
          return (
            <div key={key}
              onClick={(e) => { if (e.target === e.currentTarget && onNewSession) onNewSession(key); }}
              style={{
                minHeight: view === "week" ? 200 : 104, padding: 8, borderRadius: 12, cursor: onNewSession ? "pointer" : "default",
                border: `1px solid ${isToday ? "var(--primary,#6366f1)" : "var(--border,rgba(120,120,120,0.18))"}`,
                background: !inMonth ? "var(--bg-secondary,rgba(120,120,120,0.05))"
                  : isToday ? "color-mix(in srgb, var(--primary,#6366f1) 8%, transparent)"
                  : isWeekend ? "var(--bg-secondary,rgba(120,120,120,0.04))" : "var(--bg-primary,transparent)",
                opacity: inMonth ? 1 : 0.5,
              }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, pointerEvents: "none" }}>
                <span style={{
                  fontSize: 13, fontWeight: isToday ? 800 : 600,
                  color: isToday ? "var(--primary,#6366f1)" : "var(--text-primary)",
                  width: 24, height: 24, display: "inline-flex", alignItems: "center", justifyContent: "center",
                  borderRadius: "50%", background: isToday ? "color-mix(in srgb, var(--primary,#6366f1) 18%, transparent)" : "transparent",
                }}>{d.getDate()}</span>
                {events.length > 0 && <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{events.length}</span>}
              </div>
              {events.slice(0, max).map((s) => <Chip key={s.id} s={s} compact={view === "month"} />)}
              {events.length > max && (
                <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>+{events.length - max} więcej</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Event detail popover */}
      {selected && (
        <div onClick={() => setSelected(null)}
          style={{ position: "fixed", inset: 0, zIndex: 120, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 380, background: "var(--bg-primary,#fff)", borderRadius: 16, padding: 20, boxShadow: "0 20px 50px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ width: 12, height: 12, borderRadius: 4, background: STATUS_COLOR[selected.status] || "#6366f1" }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>{STATUS_LABEL[selected.status] || selected.status}</span>
              <button type="button" onClick={() => setSelected(null)} style={{ marginLeft: "auto", border: "none", background: "transparent", cursor: "pointer", color: "var(--text-secondary)" }}><X size={18} /></button>
            </div>
            <h3 style={{ margin: "0 0 10px", fontSize: 19, fontWeight: 700 }}>{selected.client_name}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14, color: "var(--text-secondary)", marginBottom: 16 }}>
              {selected.trainer_name && <span style={{ display: "flex", alignItems: "center", gap: 8 }}><Users size={15} />{selected.trainer_name}</span>}
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}><Clock size={15} />
                {new Date(selected.date + "T00:00").toLocaleDateString("pl-PL", { weekday: "long", day: "numeric", month: "long" })}, {selected.time} · {selected.duration} min
              </span>
              <span>{sessionTypeLabels[selected.type] || selected.type}</span>
              {selected.location_name && <span style={{ display: "flex", alignItems: "center", gap: 8 }}><MapPin size={15} />{selected.location_name}</span>}
              {selected.notes && <span style={{ fontStyle: "italic" }}>{selected.notes}</span>}
            </div>
            {onEventAction && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {selected.status === "pending_confirmation" && (<>
                  <ActBtn color="#22c55e" icon={Check} label="Potwierdź" onClick={() => { onEventAction("confirm", selected); setSelected(null); }} />
                  <ActBtn color="#ef4444" icon={X} label="Odrzuć" onClick={() => { onEventAction("reject", selected); setSelected(null); }} />
                </>)}
                {(selected.status === "scheduled" || selected.status === "confirmed") && (<>
                  <ActBtn color="#22c55e" icon={Check} label="Zakończ" onClick={() => { onEventAction("complete", selected); setSelected(null); }} />
                  <ActBtn color="#64748b" icon={Users} label="Zastąp" onClick={() => { onEventAction("reassign", selected); setSelected(null); }} />
                  <ActBtn color="#ef4444" icon={X} label="Odwołaj" onClick={() => { onEventAction("cancel", selected); setSelected(null); }} />
                </>)}
                <ActBtn color="#94a3b8" icon={Trash2} label="Usuń" onClick={() => { onEventAction("delete", selected); setSelected(null); }} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn = {
  width: 36, height: 36, borderRadius: 8, border: "1px solid var(--border,rgba(120,120,120,0.2))",
  background: "var(--bg-primary,transparent)", color: "var(--text-primary)", cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
};

const ActBtn = ({ color, icon: Icon, label, onClick }) => (
  <button type="button" onClick={onClick}
    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#fff", background: color }}>
    <Icon size={14} /> {label}
  </button>
);
